import React, { useState, useMemo, useEffect, useRef } from "react";
import { toast } from "react-toastify";
import { Wallet, HandCoins, CreditCard, Banknote } from "lucide-react";
import axios from "axios";
import { loadStripe } from "@stripe/stripe-js";
import {
  Elements,
  PaymentElement,
  useStripe,
  useElements,
} from "@stripe/react-stripe-js";
import { useSettings } from "../context/SettingsContext";
import SquarePaymentPortal from "./SquarePaymentPortal";

/* ------------------ PAYMENT METHODS ------------------ */

const BASE_PAYMENT_METHODS = [
  { id: "cod", label: "Cash on Delivery", icon: Wallet, type: "cash" },
  { id: "collection", label: "Pay on Collection", icon: HandCoins, type: "cashoncollection" },
];

/* ------------------ PLACE ORDER ------------------ */

const placeOrder = async ({ orderId, amount, type, response }) => {
  try {
    const { data } = await axios.post(`${window.BASE_API_URL}place-order`, {
      orderId,
      amount,
      type,
      response,
    });

    if (data?.success) {
      return { success: true, orderId: data.id, orderNo: data.order_no };
    }
    toast.error("❌ Payment failed");
  } catch {
    toast.error("❌ Payment error");
  }
  return { success: false };
};

/* ------------------ STRIPE FORM ------------------ */

const StripeForm = ({
  orderId,
  amount,
  onSuccess,
  onBack,
  onClose,
  isSingleMethod,
  onReady,
}) => {
  const stripe = useStripe();
  const elements = useElements();
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!stripe || !elements) return;

    setLoading(true);

    const { error, paymentIntent } = await stripe.confirmPayment({
      elements,
      redirect: "if_required",
    });

    setLoading(false);

    if (error) {
      toast.error(error.message);
    } else {
      const res = await placeOrder({
        orderId,
        amount,
        type: "stripe",
        response: paymentIntent,
      });
      if (res.success) onSuccess(res);
    }
  };

  return (
    <form onSubmit={submit} className="space-y-4">
      <PaymentElement onReady={onReady} />

      <div className="flex gap-3">
        <button
          type="button"
          onClick={isSingleMethod ? onClose : onBack}
          className="w-1/2 bg-gray-200 py-2 rounded"
        >
          {isSingleMethod ? "Cancel" : "Back"}
        </button>

        <button
          type="submit"
          disabled={!stripe || loading}
          className="w-1/2 bg-blue-600 text-white py-2 rounded"
        >
          {loading ? "Processing…" : "Pay"}
        </button>
      </div>
    </form>
  );
};

/* ------------------ MAIN MODAL ------------------ */

const PaymentModal = ({ isOpen, onClose, orderId, amount, onSuccess, paymentType }) => {
  const { settings } = useSettings();

  const [selectedMethod, setSelectedMethod] = useState("");
  const [clientSecret, setClientSecret] = useState("");
  const [stripePromise, setStripePromise] = useState(null);
  const [autoPayLoading, setAutoPayLoading] = useState(false);
  const [autoTriggered, setAutoTriggered] = useState(false);
  const [showSquare, setShowSquare] = useState(false);

  const paymentLock = useRef(false);   // 🔐 GLOBAL PAYMENT LOCK

  /* ---------- RESET WHEN MODAL OPENS ---------- */

  useEffect(() => {
    if (isOpen) {
      paymentLock.current = false;
      setAutoTriggered(false);
      setSelectedMethod("");
      setClientSecret("");
      setAutoPayLoading(false);
    }
  }, [isOpen]);

  /* ---------- PAYMENT OPTIONS ---------- */

  const filteredMethods = useMemo(() => {
    let m = [...BASE_PAYMENT_METHODS];
    if (settings?.is_stripe == 1) m.push({ id: "stripe", label: "Stripe", icon: CreditCard, type: "card" });
    if (settings?.is_windcave == 1) m.push({ id: "windcave", label: "Windcave", icon: Banknote, type: "card" });
    if (settings?.is_squareup == 1) m.push({ id: "squareup", label: "Square", icon: Banknote, type: "card" });
    if (settings?.is_xendit == 1)
  m.push({
    id: "xendit",
    label: "Xendit",
    icon: Banknote,
    type: "card",
  });

    if (paymentType !== "all") m = m.filter(x => x.type === paymentType);
    return m;
  }, [paymentType, settings]);

  /* ---------- AUTO SELECT (SAFE) ---------- */

  useEffect(() => {
    if (
      !isOpen ||
      autoTriggered ||
      filteredMethods.length !== 1 ||
      paymentLock.current
    ) return;

    const method = filteredMethods[0].id;
    setAutoTriggered(true);
    setSelectedMethod(method);
    handlePayment(method);
  }, [filteredMethods, isOpen]);

  /* ---------- PAYMENT HANDLER (LOCKED) ---------- */

  const handlePayment = async (method = selectedMethod) => {
    if (!method) return toast.error("Select payment");
    if (paymentLock.current) return;        // 🚫 block duplicates

    paymentLock.current = true;             // 🔐 LOCK
    setAutoPayLoading(true);

    if (["cod", "collection"].includes(method)) {
      const res = await placeOrder({ orderId, amount, type: method });
      setAutoPayLoading(false);
      paymentLock.current = false;          // 🔓 RELEASE
      if (res.success) onSuccess(res);
      return;
    }

    if (method === "stripe") {
      try {
        const { data } = await axios.post(`${window.BASE_API_URL}stripeintents`, {
          orderId,
          amount,
          currency: localStorage.getItem("currency_name"),
        });

        setClientSecret(data.client_secret);
        if (!stripePromise) {
          setStripePromise(await loadStripe(settings.stripe_api_key));
        }
      } catch {
        toast.error("Stripe init failed");
        setAutoPayLoading(false);
        paymentLock.current = false;
      }
      return;
    }

    if (method === "windcave") {
      const { data } = await axios.post(`${window.BASE_API_URL}windcave_request`, {
        orderno: orderId,
        amount: amount.toFixed(2),
      });
      window.location.href = data.link;
      return;
    }

    if (method === "squareup") {
      setShowSquare(true);
      setAutoPayLoading(false);
    }

    if (method === "xendit") {
  try {
    const { data } = await axios.post(
      `${window.BASE_API_URL}xendit/create-invoice`,
      {
        orderId,
        amount,
        currency: localStorage.getItem("currency_name"),
      }
    );

    // Redirect user to Xendit hosted checkout
    window.location.href = data.invoice_url;
  } catch (e) {
    toast.error("Xendit initialization failed");
    setAutoPayLoading(false);
    paymentLock.current = false;
  }
  return;
}

  };

  if (!isOpen) return null;

  return (
    <>
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
        <div className="bg-white p-6 rounded w-[420px]">

          {autoPayLoading && (
            <div className="flex flex-col items-center py-10">
              <div className="w-10 h-10 border-4 border-blue-600 border-t-transparent rounded-full animate-spin" />
              <p className="mt-3">Initializing payment…</p>
            </div>
          )}

          {!clientSecret && !autoPayLoading && (
            <>
              {filteredMethods.map(m => (
                <button
                  key={m.id}
                  onClick={() => setSelectedMethod(m.id)}
                  className="w-full p-3 border rounded mb-2"
                >
                  {m.label}
                </button>
              ))}
              <button
                onClick={() => handlePayment()}
                className="w-full bg-blue-600 text-white py-2 mt-2 rounded"
              >
                Continue
              </button>
            </>
          )}

          {clientSecret && stripePromise && (
            <Elements stripe={stripePromise} options={{ clientSecret }}>
              <StripeForm
                orderId={orderId}
                amount={amount}
                onSuccess={onSuccess}
                onBack={() => {
                  paymentLock.current = false;
                  setClientSecret("");
                }}
                onClose={onClose}
                isSingleMethod={filteredMethods.length === 1}
                onReady={() => setAutoPayLoading(false)}
              />
            </Elements>
          )}
        </div>
      </div>

      <SquarePaymentPortal
        open={showSquare}
        orderId={orderId}
        amount={amount}
        onSuccess={onSuccess}
        onClose={() => setShowSquare(false)}
      />
    </>
  );
};

export default PaymentModal;
